import { awscdk, Dependencies, ReleasableCommits } from "projen";
import { BuildWorkflow } from "projen/lib/build";
import {
  NodePackageManager,
  Transform,
  UpgradeDependenciesSchedule,
} from "projen/lib/javascript";
import { ReleaseTrigger } from "projen/lib/release";
import { merge } from "ts-deepmerge";
import { CHANGELOG_TYPES, resolveVersionrcOptions } from "./changelog-types";
import { MonorepoProject } from "./monorepo-project";
import { applyPackagePrivate, PackagePrivateOptions } from "./package-private";
import { resolveAwsCdkProjectOutdir } from "./resolve-outdir";
import { TestRunner } from "./typescript-project";
import { AgentConfig } from "../agent/agent-config";
import { AgentConfigOptions } from "../agent/agent-config-options";
import {
  AwsDeploymentConfig,
  AwsDeploymentTarget,
  AwsDeploymentTargetOptions,
  CdkCli,
  CdkCliOptions,
} from "../aws";
import { PnpmWorkspace } from "../pnpm";
import { ResetTask, ResetTaskOptions } from "../tasks/reset-task";
import { TurboRepo } from "../turbo";
import { VERSION } from "../versions";
import { Vitest, VitestOptions } from "../vitest";
import {
  AwsDeployWorkflow,
  DeployDiffOptions,
  DeployWorkflowOptions,
} from "../workflows/aws-deploy-workflow";
import {
  AwsTeardownWorkflow,
  AwsTeardownWorkflowOptions,
} from "../workflows/aws-teardown-workflow";

/**
 * Configuration options for AwsCdkProject.
 *
 * AwsCdkProject must be parented to a `MonorepoProject` — the constructor
 * throws otherwise. Configulator assumes a single-package repo is still a
 * monorepo (with one workspace) so all conventions hold uniformly.
 */
export interface AwsCdkProjectOptions
  extends
    Omit<
      awscdk.AwsCdkTypeScriptAppOptions,
      "defaultReleaseBranch" | "cdkVersion"
    >,
    PackagePrivateOptions {
  /**
   * Minimum version of `aws-cdk-lib` to depend on.
   *
   * Optional, and normally omitted: the rendered specifier is `catalog:`
   * whenever the parent `MonorepoProject`'s pnpm catalog carries an entry, so
   * that catalog decides the resolved version. Supplying a value that
   * disagrees with the entry throws rather than being silently discarded —
   * bump the catalog instead. Only where the catalog has no entry at all does
   * this value render, and then it must be an exact version.
   *
   * Listing `aws-cdk-lib` in `deps`, `devDeps`, or `peerDeps` is never the way
   * to move it — the first is overridden by the pin and the other two throw.
   *
   * @default - the parent monorepo's catalog pin for `aws-cdk-lib`
   */
  readonly cdkVersion?: string;

  /**
   * Minimum version of the `constructs` library to depend on.
   *
   * Catalog-pinned on the same terms as `cdkVersion` — omit it, or keep it
   * in step with the parent monorepo's catalog entry for `constructs`.
   *
   * @default - the parent monorepo's catalog pin for `constructs`
   */
  readonly constructsVersion?: string;

  /**
   * Options for the `CdkCli` component that every `cdk` invocation this
   * project renders flows through — per-stage command defaults, per-account
   * overrides, and the pinned CLI version.
   *
   * The component has to be seeded here rather than constructed afterwards.
   * This constructor creates an `AwsDeploymentConfig`, which auto-creates a
   * default `CdkCli` when the project has none, and `CdkCli.of()` resolves to
   * the *first* matching component — so a `new CdkCli(project, options)` built
   * once the constructor has returned is silently unreachable: no error, no
   * warning, and the generated workflows render as if nothing was passed.
   *
   * Use it to declare a setting once per project instead of repeating it on
   * every target via `AwsDeploymentTargetOptions.cdkOptions` — for example
   * pinning `cdk diff --method=change-set` across stages so a diff never
   * degrades to a template-only comparison and reports it as authoritative.
   *
   * @see `docs/src/content/docs/packages/@codedrifters/configulator/cdk-cli.md`
   * @default - a `CdkCli` carrying no consumer overrides, identical to the one
   * `AwsDeploymentConfig` would have auto-created
   */
  readonly cdkCliOptions?: CdkCliOptions;

  /**
   * AWS deployment targets (dev / stage / prod accounts and regions). Each
   * entry creates an `AwsDeploymentTarget` component on the project.
   */
  readonly deploymentTargets?: Array<AwsDeploymentTargetOptions>;

  /**
   * AWS deploy workflows. If omitted, one workflow is auto-generated per
   * unique `awsStageType` present in `deploymentTargets`. Each auto-generated
   * workflow renders to its own `.github/workflows/*.yml` file.
   *
   * Pass an empty array (`[]`) to opt out of auto-derivation entirely without
   * supplying any workflows of your own — the array is truthy and short-circuits
   * the default. Useful when you intend to attach deploy jobs to an existing
   * build workflow yourself.
   */
  readonly deployWorkflows?: Array<DeployWorkflowOptions>;

  /**
   * Opt every auto-derived deploy workflow into a `cdk diff` job per target.
   *
   * Forwarded onto each workflow derived from `deploymentTargets`. An explicit
   * `deployWorkflows[].diff` overrides it for that stage, matching the
   * `buildWorkflowOptions` precedence rule.
   *
   * `environment` is not usable here: auto-derived workflows set no `gate`, so
   * there is no environment for the diff jobs to borrow and the flag fails
   * synth. Set it on the individual `deployWorkflows` entry that gates.
   *
   * @see `docs/packages/@codedrifters/configulator/workflows/aws-deploy-workflow.md`
   * @default - no diff jobs are emitted
   */
  readonly diff?: DeployDiffOptions;

  /**
   * Optional shared `BuildWorkflow` to attach all auto-derived deploy jobs to.
   * When set, every workflow generated from `deploymentTargets` re-uses this
   * build workflow instead of creating its own. Default behavior (omit) is one
   * separate yml file per stage type.
   *
   * Distinct from `buildWorkflow: boolean` inherited from
   * `AwsCdkTypeScriptAppOptions`, which toggles whether the projen-managed
   * build workflow is generated at all.
   */
  readonly sharedBuildWorkflow?: BuildWorkflow;

  /**
   * Test runner to use.
   *
   * @default TestRunner.JEST
   */
  readonly testRunner?: TestRunner;

  /**
   * Options for Vitest (only used when testRunner is 'vitest').
   */
  readonly vitestOptions?: VitestOptions;

  /**
   * Enable the reset task that deletes all build artifacts.
   *
   * @default true
   */
  readonly resetTask?: boolean;

  /**
   * Options for the reset task.
   */
  readonly resetTaskOptions?: ResetTaskOptions;

  /**
   * AI agent configuration (Cursor, Claude Code rules). Opt-in per project;
   * not inherited from the parent `MonorepoProject`.
   *
   * @default false
   */
  readonly agentConfig?: AgentConfigOptions | boolean;

  /**
   * Scheduled teardown workflow options. When provided, an `AwsTeardownWorkflow`
   * is attached to the parent `MonorepoProject` that periodically deletes
   * orphaned CloudFormation stacks whose branch no longer exists.
   */
  readonly teardownWorkflow?: AwsTeardownWorkflowOptions;
}

/**
 * AWS CDK TypeScript application with CodeDrifters conventions baked in.
 *
 * Always creates an `AwsDeploymentConfig` component (even with zero deployment
 * targets) so the `synth` task is consistently rewritten to emit into
 * `dist/<project-path>/cdk.out`. This is intentional and not opt-out: it
 * funnels every package's CDK output into one root-level `dist/` tree so
 * GitHub Actions workflows can pass artifacts between jobs uniformly.
 */
export class AwsCdkProject extends awscdk.AwsCdkTypeScriptApp {
  constructor(userOptions: AwsCdkProjectOptions) {
    /**
     * Configulator requires every project to live inside a MonorepoProject.
     * The catalog-based `@types/node` pin, the workspace install hook, and the
     * deploy workflow all assume the parent exists.
     */
    if (!(userOptions.parent instanceof MonorepoProject)) {
      throw new Error(
        "AwsCdkProject must be parented to a MonorepoProject. Pass `parent: <MonorepoProject>` in the project options.",
      );
    }

    const parent = userOptions.parent;
    const pnpmVersion = parent.pnpmVersion;
    const pnpmWorkspace = PnpmWorkspace.of(parent);
    const defaultCatalog = pnpmWorkspace?.defaultCatalog ?? {};

    /*************************************************************************
     *
     * Reject catalog-managed packages in `devDeps` / `peerDeps`
     *
     * The pin re-registers both packages as RUNTIME deps after `super()`, and
     * `Dependencies.addDependency` keys its replace-vs-append decision on name
     * *and* type — so only a RUNTIME entry is replaced. A `devDeps` or
     * `peerDeps` entry survives it and renders a second, uncontrolled
     * specifier beside the pin. Reject those the same way an out-of-step
     * `cdkVersion` is rejected, rather than discard them silently.
     *
     ************************************************************************/

    rejectCatalogManagedDeps("devDeps", userOptions.devDeps);
    rejectCatalogManagedDeps("peerDeps", userOptions.peerDeps);

    /*************************************************************************
     *
     * Resolve CDK versions from the catalog
     *
     * `aws-cdk-lib` and `constructs` are re-registered as `catalog:` after
     * `super()`, so the parent monorepo's catalog — not these options —
     * decides what actually resolves. Read the versions back out of that
     * catalog so projen's own view of them can never drift from what gets
     * installed, and reject both ways it could stop being a real pin: a
     * range-valued catalog entry, or an explicit option that disagrees.
     *
     * Each call also yields the specifier to render, which is `catalog:` only
     * while the catalog actually carries an entry to resolve through.
     *
     ************************************************************************/

    const cdkPin = resolveCatalogPin(
      "cdkVersion",
      "aws-cdk-lib",
      userOptions.cdkVersion,
      VERSION.AWS_CDK_LIB_VERSION,
      defaultCatalog,
    );
    const constructsPin = resolveCatalogPin(
      "constructsVersion",
      "constructs",
      userOptions.constructsVersion,
      VERSION.AWS_CONSTRUCTS_VERSION,
      defaultCatalog,
    );
    const cdkVersion = cdkPin.version;
    const constructsVersion = constructsPin.version;

    /*************************************************************************
     *
     * Resolve outdir
     *
     * When the caller omits `outdir`, derive it from the scoped package
     * name (`packageName ?? name`) as `apps/<scope>/<name>`. This enforces
     * the monorepo-layout contract (ADR-006) without requiring every
     * sub-project to restate its own path.
     *
     ************************************************************************/

    const resolvedOutdir =
      userOptions.outdir ??
      resolveAwsCdkProjectOutdir(userOptions.packageName ?? userOptions.name);

    /*************************************************************************
     *
     * Default Options
     *
     ************************************************************************/

    const testRunner = userOptions.testRunner ?? TestRunner.JEST;
    const useJest = testRunner === TestRunner.JEST;

    const defaultOptions: Omit<AwsCdkProjectOptions, "name"> & {
      defaultReleaseBranch: string;
      cdkVersion: string;
    } = {
      defaultReleaseBranch: "main",

      /**
       * Outdir defaults to `apps/<scope>/<name>` when the caller omits
       * it. Explicit overrides in `userOptions` always win via the
       * merge below.
       */
      outdir: resolvedOutdir,

      /**
       * Enable reset task by default.
       */
      resetTask: true,

      /**
       * Packaging options.
       */
      packageManager: NodePackageManager.PNPM,
      pnpmVersion: pnpmVersion,
      licensed: userOptions.license !== undefined || false,
      copyrightOwner: "CodeDrifters",
      release: false,

      /**
       * Don't add sample code.
       */
      sampleCode: false,

      /**
       * CDK versions read back out of the catalog above. The framework and
       * its `constructs` peer render as `catalog:` (see after `super()`);
       * these keep projen's own view of the versions in step with whatever
       * that catalog pins, rather than its `^10.5.1`-style fallbacks.
       */
      cdkVersion,
      constructsVersion,
      cdkCliVersion: VERSION.AWS_CDK_CLI_VERSION,

      ...(useJest
        ? {
            /**
             * Make sure jest config is stored outside of package.json and use
             * SWC for faster compilation (matches TypeScriptProject).
             */
            jestOptions: {
              configFilePath: "jest.config.json",
              jestConfig: {
                roots: [`<rootDir>/src`],
                transform: {
                  ["^.+\\.[t]sx?$"]: new Transform("@swc/jest"),
                },
                moduleFileExtensions: ["js", "ts"],
              },
            },
            devDeps: ["@swc/jest", "@swc/core"] as Array<string>,
          }
        : {
            jest: false,
            devDeps: [] as Array<string>,
          }),

      /**
       * Turn on prettier formatting.
       */
      prettier: true,

      /**
       * Don't package test files.
       */
      npmIgnoreOptions: {
        ignorePatterns: ["*.spec.*", "*.test.*"],
      },

      /**
       * Tests are co-located in `src`, so projen 0.100.4+'s test-directory
       * tsconfig would only ever create an empty `test/` folder holding a
       * lone `test/tsconfig.json`. Disable it — the main `tsconfig.json`
       * already covers the co-located tests for the typescript-eslint
       * project service (see #830), so a separate dev/test config is dead
       * weight.
       */
      disableTsconfigDev: true,

      /**
       * Options for the automated dependency upgrade task / workflow.
       * Exclude any packages managed by the parent project's default catalog
       * so the catalog stays the source of truth.
       */
      depsUpgrade: true,
      depsUpgradeOptions: {
        workflow: false,
        exclude: Object.keys(defaultCatalog),
        workflowOptions: {
          schedule: UpgradeDependenciesSchedule.WEEKLY,
        },
        cooldown: 7,
      },

      /**
       * Only release when the package's own sources or `package.json`
       * (version, dependencies) change.
       *
       * Deliberately narrower than the `"."` pathspec the bump decision and
       * the changelog use below. Widening this to `<outdir>/**` would make
       * all three filters select exactly the same files, but it also makes
       * every package-local edit releasable — a test-only change, or the
       * `.projen/` regeneration that a sibling package's change produces. In
       * this monorepo that regeneration is routine, so the aligned trigger
       * publishes versions whose tarballs are byte-identical to the previous
       * one.
       *
       * The asymmetry that remains is benign: a package-local commit outside
       * `src` cannot start a release on its own, but is still described in
       * the notes of the next release that something else triggers. Notes
       * covering slightly more than the trigger is the right direction for
       * the two to disagree in.
       */
      releaseTrigger: ReleaseTrigger.continuous({
        paths: [`${resolvedOutdir}/src/**`, `${resolvedOutdir}/package.json`],
      }),

      /**
       * Scope the other two release filters to the package folder, the same
       * slice of history the trigger above reads from.
       *
       * `versionrcOptions.path` reaches `commit-and-tag-version`, which
       * forwards it to `conventional-changelog` as a `git log` pathspec —
       * without it the changelog between two package-scoped tags spans every
       * commit in the monorepo. `releasableCommits` appends the same pathspec
       * to the command that decides whether to bump at all; without it a
       * package with no relevant changes still cuts a release, just with an
       * empty changelog.
       *
       * `"."` rather than `resolvedOutdir`: both commands run with their
       * working directory already set to the package outdir. A single string
       * is all `path` accepts — `git-raw-commits` splices it into `git log`
       * as one argv entry (`push("--", path)`), so the pathspec cannot be a
       * list, so it cannot be narrowed to `src` plus `package.json` to match
       * the trigger exactly.
       *
       * `types` gives every conventional-commit type a visible section. The
       * preset hides or discards everything but `feat` and `fix` by default,
       * while still bumping for them, which is what makes a package-local
       * churn release ship notes holding nothing but a version heading. See
       * `CHANGELOG_TYPES`.
       */
      versionrcOptions: { path: ".", types: [...CHANGELOG_TYPES] },
      releasableCommits: ReleasableCommits.everyCommit("."),
    };

    /*************************************************************************
     *
     * Merge defaults into user options
     *
     ************************************************************************/

    const mergedOptions = merge(defaultOptions, userOptions);

    const options: AwsCdkProjectOptions & awscdk.AwsCdkTypeScriptAppOptions = {
      ...mergedOptions,

      /**
       * Both versions are the catalog's, resolved above — restate them after
       * the merge so a supplied option can never reintroduce a value the
       * catalog disagrees with. Restating `cdkVersion` also keeps the
       * intersection type honest, since it is optional here but required by
       * projen.
       */
      cdkVersion,
      constructsVersion,

      /**
       * `ts-deepmerge` concatenates arrays, so a supplied changelog `types`
       * arrives appended to `CHANGELOG_TYPES` — and the preset resolves each
       * commit with `types.find(...)`, so our entry would win every lookup
       * and the consumer's list would be inert. Replace, don't concatenate.
       */
      versionrcOptions: resolveVersionrcOptions(
        mergedOptions.versionrcOptions,
        userOptions.versionrcOptions,
      ),
    };

    super(options);

    /**
     * Use catalog version for `@types/node` so all packages share a single
     * pinned version.
     */
    this.addDevDeps("@types/node@catalog:");

    /**
     * Projen's `AwsCdkDeps` renders the framework and its peer as caret
     * ranges (`aws-cdk-lib@^2.264.0`, `constructs@^10.5.1`), so pnpm floats
     * both within the major line and the configulator-managed catalog never
     * actually pins them. Re-register each against the specifier resolved
     * above — `addDependency` replaces the same name/type entry rather than
     * duplicating it, and `AwsCdkTypeScriptApp` registers both as RUNTIME
     * deps, which is what `addDeps` writes.
     */
    this.addDeps(
      `aws-cdk-lib@${cdkPin.specifier}`,
      `constructs@${constructsPin.specifier}`,
    );

    /**
     * The main tsconfig now includes co-located `src/**` test files (no
     * `**\/*.test.*` exclude) so projen 0.100.4+'s typescript-eslint project
     * service can resolve them (issue #830). Because `tsc --build` also uses
     * the main tsconfig, skip type-checking of dependency declaration files
     * so the compile doesn't fail on test-runner types — Vitest pulls in
     * Vite's `.d.ts`, which don't type-check under the package's classic
     * `node` moduleResolution.
     */
    this.tsconfig?.file.addOverride("compilerOptions.skipLibCheck", true);

    /**
     * When using Jest, remove ts-jest since we use `@swc/jest`.
     * When using Vitest, add the Vitest component (test task + config + deps).
     */
    if (testRunner === TestRunner.VITEST) {
      new Vitest(this, {
        ...options.vitestOptions,
        config: {
          include: ["src/**/*.{test,spec}.?(c|m)[jt]s?(x)"],
          ...options.vitestOptions?.config,
        },
      });
    } else {
      this.deps.removeDependency("ts-jest");
    }

    /**
     * Specify package manager in package.json.
     */
    this.package.file.addOverride(
      "packageManager",
      `pnpm@${options.pnpmVersion}`,
    );

    /**
     * A CDK app is deployed, not published, so it renders `"private": true`
     * by default and npm refuses to publish it by hand. Pass
     * `packagePrivate: false` to opt out.
     */
    applyPackagePrivate(this, options);

    /**
     * Allow test files to use devDependencies. Projen disables the rule for
     * `tests/*` only; we colocate tests next to source so we extend it.
     */
    this.eslint?.addOverride({
      files: ["**/*.test.*", "**/*.spec.*"],
      rules: {
        "import/no-extraneous-dependencies": "off",
      },
    });

    /**
     * Some standard ignores for all projects.
     */
    this.gitignore?.addPatterns(".DS_Store", "test-reports", "cdk.out");
    this.npmignore?.addPatterns("*.spec.*", "*.test.*", "__fixtures__");

    /**
     * Seed the CdkCli before AwsDeploymentConfig gets the chance to auto-create
     * a default one. That config resolves the component as
     * `CdkCli.of(this) || new CdkCli(this)`, and `CdkCli.of` returns the first
     * match — so the slot is taken for good by whichever instance exists first,
     * and anything a consumer constructs after this constructor returns is
     * silently unreachable. Constructed unconditionally to keep a single code
     * path: with no `cdkCliOptions` this is exactly the empty-option instance
     * AwsDeploymentConfig would have built for itself on the next line.
     */
    new CdkCli(this, options.cdkCliOptions ?? {});

    /**
     * Always create the AwsDeploymentConfig so the synth task is rewritten
     * consistently. AwsDeploymentTarget instances will register themselves
     * onto this config below.
     */
    new AwsDeploymentConfig(this);

    /**
     * Wire up deployment targets supplied via constructor options.
     */
    (options.deploymentTargets ?? []).forEach((targetOptions) =>
      this.addDeploymentTarget(targetOptions),
    );

    /**
     * Wire up deploy workflows. If `deployWorkflows` is undefined, derive one
     * workflow per unique awsStageType present in the deployment targets.
     * An explicit empty array opts out of auto-derivation.
     */
    const targetsForAutoDerive =
      AwsDeploymentConfig.of(this)?.awsDeploymentTargets ?? [];
    const deployWorkflows =
      options.deployWorkflows ??
      Array.from(new Set(targetsForAutoDerive.map((t) => t.awsStageType))).map(
        (awsStageType): DeployWorkflowOptions => {
          // A diff job is a post-build job and conflicts with nothing a shared
          // build workflow provides, so unlike `buildWorkflowOptions` this
          // forwards on both paths below. Omit the key entirely when unset so
          // the no-options case stays byte-identical.
          const diff = options.diff ? { diff: options.diff } : {};

          // When a shared build workflow is supplied, the deploy jobs re-use
          // it and the runner is owned by that external workflow. Forwarding
          // `buildWorkflowOptions` here would trip AwsDeployWorkflow's
          // both-provided guard, so emit only the build workflow.
          if (options.sharedBuildWorkflow) {
            return {
              awsStageType,
              buildWorkflow: options.sharedBuildWorkflow,
              ...diff,
            };
          }

          // Otherwise forward the project-level `buildWorkflowOptions` (e.g.
          // `runsOn`) onto each derived deploy build job. Omit the key entirely
          // when unset so the no-options case stays byte-identical.
          return {
            awsStageType,
            ...(options.buildWorkflowOptions
              ? { buildWorkflowOptions: options.buildWorkflowOptions }
              : {}),
            ...diff,
          };
        },
      );
    deployWorkflows.forEach(
      (workflowOptions) => new AwsDeployWorkflow(this, workflowOptions),
    );

    /**
     * Optional teardown workflow. Attached to the parent MonorepoProject so
     * it renders once per repo regardless of how many AwsCdkProjects exist.
     */
    if (options.teardownWorkflow) {
      new AwsTeardownWorkflow(parent, options.teardownWorkflow);
    }

    /**
     * If turbo is active in the parent project, configure it here.
     */
    const turboActive = TurboRepo.of(parent) !== undefined;
    if (turboActive) {
      const turbo = new TurboRepo(this);
      // Only inputs here. Post-compile outputs are owned by
      // `AwsDeploymentConfig`, which registers the root-dist `cdk.out` path
      // from its own `preSynthesize` — the sole artifact post-compile writes,
      // since `synth:silent` emits there and the `package` task (the other
      // thing that would fill a local `dist/`) has no steps while `release`
      // defaults to false. A package-local `dist/**` here declared an output
      // nothing produces, so turbo cached an empty directory for it (#974).
      turbo.postCompileTask?.inputs.push("src/**");
    }

    /**
     * AI agent configuration — opt-in per project. Does not propagate from
     * the parent MonorepoProject so sub-projects don't render duplicate
     * `.cursor/` / `.claude/` / `CLAUDE.md` files into their own outdir.
     */
    if (
      options.agentConfig === true ||
      typeof options.agentConfig === "object"
    ) {
      new AgentConfig(
        this,
        typeof options.agentConfig === "object" ? options.agentConfig : {},
      );
    }

    /**
     * Add reset task if enabled.
     */
    if (options.resetTask !== false) {
      const defaultResetTaskOptions: ResetTaskOptions = {
        pathsToRemove: [
          "node_modules",
          "dist",
          "lib",
          "cdk.out",
          "coverage",
          "test-reports",
          ".turbo",
          "tsconfig.tsbuildinfo",
          this.artifactsDirectory,
        ],
      };
      const userResetTaskOptions = options.resetTaskOptions ?? {};
      const resetTaskOptions: ResetTaskOptions = merge(
        defaultResetTaskOptions,
        {
          ...userResetTaskOptions,
          pathsToRemove: [
            ...(defaultResetTaskOptions.pathsToRemove ?? []),
            ...(userResetTaskOptions.pathsToRemove ?? []),
          ],
        },
      );
      new ResetTask(this, resetTaskOptions);
    }

    /**
     * Route dependency installation through the parent MonorepoProject so the
     * workspace install handles everything in one pass.
     */
    /* @ts-ignore access private method */
    const originalResolve = this.package.resolveDepsAndWritePackageJson;
    /* @ts-ignore access private method */
    this.package.installDependencies = () => {
      parent.requestInstallDependencies({
        resolveDepsAndWritePackageJson: () =>
          originalResolve.apply(this.package),
      });
    };
    /* @ts-ignore access private method */
    this.package.resolveDepsAndWritePackageJson = () => {};
  }

  /**
   * Add an AWS deployment target to this project after construction. The
   * target is registered on the existing `AwsDeploymentConfig` and immediately
   * available for `AwsDeployWorkflow` consumption.
   */
  public addDeploymentTarget(
    options: AwsDeploymentTargetOptions,
  ): AwsDeploymentTarget {
    return new AwsDeploymentTarget(this, options);
  }
}

/**
 * A single exact version — `2.264.0`, `2.264.0-rc.1` — as opposed to any
 * range npm accepts (`^2.264.0`, `~2.264`, `>=2`, `2.x`, `*`, `latest`).
 */
const EXACT_VERSION = /^\d+\.\d+\.\d+(?:-[\w.-]+)?(?:\+[\w.-]+)?$/;

/**
 * The packages `AwsCdkProject` pins itself. A consumer entry for either can
 * only restate the pin or contradict it, never improve on it.
 */
const CATALOG_MANAGED_PACKAGES = ["aws-cdk-lib", "constructs"];

/**
 * How a catalog-managed dependency renders.
 */
interface CatalogPin {
  /** The version projen's own view of the dependency resolves to. */
  readonly version: string;

  /**
   * The specifier written into `package.json` — `catalog:` when the catalog
   * carries an entry to resolve through, otherwise the exact version itself.
   */
  readonly specifier: string;
}

/**
 * Reject any `aws-cdk-lib` / `constructs` entry in a non-RUNTIME dependency
 * array, naming the offending spec.
 *
 * Neither ever reaches the pin: `addDependency` replaces only a same-name,
 * *same-type* entry, so a `devDeps` spec renders a second specifier next to
 * the pinned one (`devDependencies: { "aws-cdk-lib": "^2.0.0" }`, or `"*"`
 * when the spec carries no version), a `peerDeps` spec does the same in
 * `peerDependencies`, and projen's own `AwsCdkDeps` deletes a `constructs`
 * dev entry outright. Every one of those discards what the consumer asked
 * for, so say so rather than let it happen quietly.
 */
function rejectCatalogManagedDeps(
  optionName: string,
  specs: Array<string> | undefined,
): void {
  for (const spec of specs ?? []) {
    const { name } = Dependencies.parseDependency(spec);

    if (!CATALOG_MANAGED_PACKAGES.includes(name)) {
      continue;
    }

    throw new Error(
      `AwsCdkProject pins "${name}" itself, as a runtime dependency on the parent MonorepoProject's catalog, ` +
        `so \`${optionName}: ["${spec}"]\` never reaches that pin — it renders a second, unpinned specifier beside it or is dropped outright. ` +
        `Remove "${spec}" from \`${optionName}\`, and change the version through the catalog entry for "${name}" instead.`,
    );
  }
}

/**
 * Resolve how `packageName` is pinned: the version projen's own view uses,
 * and the specifier rendered into `package.json`.
 *
 * Reading the version back out of the parent monorepo's catalog — rather than
 * validating a default against it — is what keeps projen's view from drifting
 * from what pnpm installs, including when a consumer overrides the catalog and
 * omits the option entirely.
 */
function resolveCatalogPin(
  optionName: string,
  packageName: string,
  suppliedVersion: string | undefined,
  fallbackVersion: string,
  catalog: { [key: string]: string },
): CatalogPin {
  const catalogVersion = catalog[packageName];

  /**
   * With no entry behind it, `catalog:` resolves to nothing and pnpm fails the
   * install on a protocol error naming neither this project nor the entry it
   * expected. Render the resolved version literally instead — that holds the
   * pin without depending on a catalog that is not there.
   */
  if (catalogVersion === undefined) {
    const version = suppliedVersion ?? fallbackVersion;

    if (!EXACT_VERSION.test(version)) {
      throw new Error(
        `AwsCdkProject pins "${packageName}" exactly, but the parent MonorepoProject's catalog carries no entry for it, ` +
          `so the rendered specifier falls back to \`${optionName}: "${version}"\` — a range, which would leave every CDK app floating within it. ` +
          `Add an exact "${packageName}" entry to the catalog, or set \`${optionName}\` to an exact version.`,
      );
    }

    return { version, specifier: version };
  }

  // `catalog:` pins only as tightly as the entry behind it, so a range there
  // silently reinstates the float the pin exists to remove.
  if (!EXACT_VERSION.test(catalogVersion)) {
    throw new Error(
      `AwsCdkProject renders "${packageName}" as "catalog:", which pins only as tightly as the catalog entry behind it. ` +
        `The parent MonorepoProject's catalog holds "${catalogVersion}" — a range, so every CDK app would keep floating within it. ` +
        `Set the catalog entry for "${packageName}" to an exact version.`,
    );
  }

  if (suppliedVersion !== undefined && suppliedVersion !== catalogVersion) {
    throw new Error(
      `AwsCdkProject renders "${packageName}" as "catalog:", so the parent MonorepoProject's catalog pin (${catalogVersion}) decides the resolved version. ` +
        `\`${optionName}: "${suppliedVersion}"\` would be silently ignored. Drop the option, or bump the catalog entry for "${packageName}" to ${suppliedVersion}.`,
    );
  }

  return { version: catalogVersion, specifier: "catalog:" };
}
